import { useNavigate } from '@tanstack/react-router'
import { type DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useDocumentSearchData } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/DocumentSearchDataContext'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useSearchActions } from 'features/knowledgeDocumentViewer/contexts/SearchActionsContext'
import { useFavoriteDocuments } from 'features/knowledgeDocumentViewer/hooks/useFavoriteDocuments'
import {
  type ChartSearchItem,
  isChartSearchItem,
  isDocumentSearchItem,
  type RecentItem,
  type SensorSearchItem,
} from 'features/knowledgeDocumentViewer/types/searchableItems'
import { SelectedTab } from 'features/navbar/types'
import { useNavbarConfig } from 'features/navbar/useNavbarConfig'
import { useNavbarData } from 'features/navbar/useNavbarData'
import { useRolodexEntries } from 'features/rolodex/hooks/useRolodexEntries'
import { publishWorkspaceTabTitle } from 'features/workspaceTabs/publishWorkspaceTabTitle'
import { useOptionalWorkspaceTabsActions } from 'features/workspaceTabs/WorkspaceTabsContext'
import React, { useCallback, useMemo } from 'react'
import { createWalkthroughSearch } from 'routes/walkthrough/-searchParams'
import { RolodexEntryType } from 'types'

import { CantileverLandingLogo } from './CantileverLandingLogo'
import { type JumpToLinkCardItem } from './JumpToLinkCards'
import { MAX_LANDING_RECENTS } from './landingScrollConstants'
import { LandingScrollSections } from './LandingScrollSections'
import { LandingSearchBar } from './LandingSearchBar'
import {
  LandingBody,
  LandingColumn,
  LandingColumnWrapper,
  LandingContainer,
  LogoSection,
} from './unifiedSearchLandingStyles'

export const UnifiedSearchLanding: React.FC = () => {
  const {
    onSelectDocument,
    onAddRecentDocument,
    onOpenSensorDashboard,
    onOpenChartDashboard,
    onOpenTagSearchDashboard,
    recentItems,
    isFavorited,
    toggleFavorite,
  } = useSearchActions()
  const { recentItems: dataRecentItems } = useDocumentSearchData()
  const { siteId } = useAdvancedSearchUX()
  const { favoriteDocuments, removeFavorite } = useFavoriteDocuments(siteId)
  const setActiveTabTitle = useOptionalWorkspaceTabsActions()?.setActiveTabTitle
  const navigate = useNavigate()
  const { orgID } = useKnowledgeDocumentDataContext()
  const { site } = useNavbarData(siteId)
  const { allowedNavbarItems, isLoading: isLoadingNavbarConfig } =
    useNavbarConfig({
      orgId: orgID,
      siteId,
    })
  const siteName = site?.data.name ?? 'Site'
  const { matterportSpaces, isLoading: isLoadingMatterportSpaces } =
    useRolodexEntries(
      siteId
        ? {
            type: RolodexEntryType.MATTERPORT_SPACE,
            tags: { site: siteId },
          }
        : undefined
    )
  const hasSiteWalkthrough = matterportSpaces.length > 0
  const isWalkthroughEnabledInNavbarConfig =
    allowedNavbarItems?.includes(SelectedTab.Walkthrough) ?? false
  const canOpenSiteWalkthrough =
    hasSiteWalkthrough && isWalkthroughEnabledInNavbarConfig
  const isCheckingWalkthroughAvailability =
    isLoadingMatterportSpaces || isLoadingNavbarConfig
  const isSiteWalkthroughDisabled =
    isCheckingWalkthroughAvailability || !canOpenSiteWalkthrough
  const siteWalkthroughDescription = isCheckingWalkthroughAvailability
    ? 'Checking walkthrough availability'
    : !hasSiteWalkthrough
      ? 'No 3D walkthrough available'
      : !isWalkthroughEnabledInNavbarConfig
        ? 'Site walkthrough is not enabled'
        : `${siteName} 3D walkthrough of site scan`
  const siteWalkthroughAriaLabel = canOpenSiteWalkthrough
    ? 'Open Site Walkthrough'
    : siteWalkthroughDescription

  const { entries: processDataEntries, isLoading: isLoadingProcessData } =
    useRolodexEntries(
      siteId
        ? {
            type: RolodexEntryType.DATA_POINTS,
            tags: { site: siteId },
            limit: 1,
          }
        : undefined
    )
  const hasProcessData = processDataEntries.length > 0
  const isProcessDataDisabled = isLoadingProcessData || !hasProcessData
  const processDataDescription = isLoadingProcessData
    ? 'Checking process data availability'
    : !hasProcessData
      ? 'No process data available'
      : `${siteName} sensor readings and time series data`
  const processDataAriaLabel = hasProcessData
    ? 'Open Process Data Viewer'
    : processDataDescription

  const allRecents = recentItems.length > 0 ? recentItems : dataRecentItems
  const displayRecentDocuments = useMemo(
    () =>
      allRecents
        .filter((recent) => isDocumentSearchItem(recent.item))
        .slice(0, MAX_LANDING_RECENTS),
    [allRecents]
  )
  const recentCharts = useMemo(
    () =>
      allRecents
        .filter((recent): recent is RecentItem<ChartSearchItem> =>
          isChartSearchItem(recent.item)
        )
        .slice(0, MAX_LANDING_RECENTS),
    [allRecents]
  )

  const handleRecentDocumentSelect = useCallback(
    (doc: DocumentTableRow) => {
      onAddRecentDocument(doc)
      onSelectDocument({ documentId: doc.id })
      const title = doc.documentTitle?.trim() || doc.filename?.trim()
      publishWorkspaceTabTitle(setActiveTabTitle, title || undefined)
    },
    [onAddRecentDocument, onSelectDocument, setActiveTabTitle]
  )

  const handleFavoriteSelect = useCallback(
    (documentId: string) => {
      onSelectDocument({ documentId })
    },
    [onSelectDocument]
  )

  const handleSensorSelect = useCallback(
    (sensor: SensorSearchItem) => {
      onOpenSensorDashboard(sensor)
    },
    [onOpenSensorDashboard]
  )

  const handleChartSelect = useCallback(
    (chart: ChartSearchItem) => {
      onOpenChartDashboard(chart)
    },
    [onOpenChartDashboard]
  )

  const handleOpenSiteWalkthrough = useCallback(() => {
    navigate({
      to: '/walkthrough',
      search: createWalkthroughSearch({
        orgId: orgID,
        siteId,
      }),
    })
  }, [navigate, orgID, siteId])

  const jumpToItems = useMemo<JumpToLinkCardItem[]>(
    () => [
      {
        title: 'Process Data',
        description: processDataDescription,
        onSelect: onOpenTagSearchDashboard,
        ariaLabel: processDataAriaLabel,
        disabled: isProcessDataDisabled,
      },
      {
        title: 'Site Walkthrough',
        description: siteWalkthroughDescription,
        onSelect: handleOpenSiteWalkthrough,
        ariaLabel: siteWalkthroughAriaLabel,
        disabled: isSiteWalkthroughDisabled,
      },
    ],
    [
      handleOpenSiteWalkthrough,
      isProcessDataDisabled,
      isSiteWalkthroughDisabled,
      onOpenTagSearchDashboard,
      processDataAriaLabel,
      processDataDescription,
      siteWalkthroughAriaLabel,
      siteWalkthroughDescription,
    ]
  )

  return (
    <LandingContainer>
      <LogoSection>
        <CantileverLandingLogo />
      </LogoSection>
      <LandingColumnWrapper>
        <LandingColumn>
          <LandingBody>
            <LandingSearchBar />
            <LandingScrollSections
              jumpToItems={jumpToItems}
              favoriteDocuments={favoriteDocuments}
              displayRecentDocuments={displayRecentDocuments}
              recentCharts={recentCharts}
              onFavoriteSelect={handleFavoriteSelect}
              onRemoveFavorite={removeFavorite}
              onRecentDocumentSelect={handleRecentDocumentSelect}
              onSensorSelect={handleSensorSelect}
              onChartSelect={handleChartSelect}
              isFavorited={isFavorited}
              onToggleFavorite={toggleFavorite}
            />
          </LandingBody>
        </LandingColumn>
      </LandingColumnWrapper>
    </LandingContainer>
  )
}
